/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { isEmpty, isNone } from '@ember/utils';
import { TestSetupArgs } from 'cio/components/newsletters/test-setup';
import { tracked } from '@glimmer/tracking';

const DURATION_TYPE_HOUR = 'hour';
const DURATION_TYPE_DAY = 'day';

const SECONDS_IN_HOUR = 60 * 60;
const HOURS_IN_DAY = 24;
const SECONDS_IN_DAY = SECONDS_IN_HOUR * HOURS_IN_DAY;

interface Duration {
  value: number | null;
  type: string | null;
}

export default class ChooseWinnerAutomatically extends Component<TestSetupArgs> {
  readonly testMetrics = Object.freeze([
    { label: 'By click rate', value: 'clicked' },
    { label: 'By open rate', value: 'opened' },
    { label: 'By conversion rate', value: 'converted' },
  ]);
  readonly durationTypes = Object.freeze([
    { label: 'hours', value: DURATION_TYPE_HOUR },
    { label: 'days', value: DURATION_TYPE_DAY },
  ]);
  durationValue: number | null = null;
  @tracked testDuration: Duration = {
    value: null,
    type: null,
  };

  constructor(owner: unknown, args: TestSetupArgs) {
    super(owner, args);
    this.findDurationUnitAndValue();
  }

  private findDurationUnitAndValue() {
    let { testDuration } = this.args.newsletter;

    if (isEmpty(testDuration)) {
      this.testDuration = { value: null, type: null };
      return;
    }

    let testDurationInHours = testDuration / SECONDS_IN_HOUR;
    let duration;

    if (testDurationInHours % HOURS_IN_DAY === 0) {
      duration = {
        value: testDurationInHours / HOURS_IN_DAY,
        type: DURATION_TYPE_DAY,
      };
    } else {
      duration = {
        value: testDurationInHours,
        type: DURATION_TYPE_HOUR,
      };
    }

    this.testDuration = duration;
  }

  private setTestDurationInSeconds(durationValue: number | null, durationUnits: Duration['type']) {
    let { newsletter, validate } = this.args;
    let durationInSeconds;

    if (isNone(durationValue) || ![DURATION_TYPE_DAY, DURATION_TYPE_HOUR].includes(durationUnits as string)) {
      return;
    }

    if (durationUnits === DURATION_TYPE_HOUR) {
      durationInSeconds = durationValue * SECONDS_IN_HOUR;
    } else if (durationUnits === DURATION_TYPE_DAY) {
      durationInSeconds = durationValue * SECONDS_IN_DAY;
    }

    newsletter.set('testDuration', durationInSeconds);
    validate('testDuration');
  }

  @action
  didSelectDuration(event: Event) {
    let duration: string | number = (event.target as HTMLInputElement).value;
    if (duration != null) {
      duration = Number(duration);
    }
    this.durationValue = duration;
    this.setTestDurationInSeconds(duration || 0, this.testDuration.type);
  }

  @action
  didSelectDurationType(type: (typeof this.durationTypes)[number]) {
    this.setTestDurationInSeconds(this.testDuration.value ?? this.durationValue, type.value);
  }

  @action
  didSelectTestMetric(metric: (typeof this.testMetrics)[number]) {
    let { newsletter, validate } = this.args;
    newsletter.set('testMetric', metric.value);
    validate('testMetric');
  }

  @action
  toggleAutomaticWinner() {
    let { newsletter } = this.args;
    if (!newsletter.automaticWinner) {
      newsletter.set('automaticWinner', true);
    } else {
      newsletter.setProperties({
        automaticWinner: false,
        testMetric: null,
        testDuration: null,
      });
    }
  }
}
